import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import requests from "./api/client"
import { isPhysicalChipAvailable, normalizePhysicalChipEnabled } from './utils/chipAvailability'
import { normalizeReferralVoucherEnabled } from './utils/referralVoucher'
import { isUnixcardEnabled } from './utils/unixcard'
import { useForm } from "./Form"
import dayjs from "dayjs"

interface UserConfigParamProps {
  id: string | number
  tipo: string
}

export interface StatesListProps {
  estado?: string,
  uf?: string,
  label?: string,
}

interface LogisticPlans {
  id: string
  plano: string
  valor: string
  repasse_mvno_ativacao_chip_fisico: string
  repasse_mvno_ativacao_chip_esim: string
  repasse_mvno_logistica_chip_plano: string
} 

export interface LogisticProps {
  habilitar_venda_somente_chip?: boolean,
  valor_chip?: string,
  valor_frete?: string
  valor_chip_avulso?: string,
  valor_frete_avulso?: string
  repasse_mvno_logistica_chip_avulso?: number
  habilitar_retirada_na_mvno?: boolean
  planos?: LogisticPlans[]
}

/**
 * Recorte de GET /busca-uuid. O endpoint devolve a configuracao completa da
 * marca (cores, copyright, urls, token), mas aqui so consumimos as flags de
 * plano anual, de venda de chip fisico e do sistema de indicacao. Os campos
 * sao opcionais de proposito: se a API ainda nao estiver atualizada, a
 * ausencia de venda_chip_fisico nao pode desabilitar o chip fisico de todas
 * as marcas. Ja a ausencia de sistema_indicacao desliga o voucher (ver
 * utils/referralVoucher).
 */
export interface BrandConfigProps {
  /** 'mvno' na busca pela marca; com `id`, identifica a marca (ver utils/unixcard). */
  tipo?: string
  id?: number | string
  anual?: true
  venda_chip_fisico?: boolean
  sistema_indicacao?: boolean
}

export interface ChipInfo {
  chipOnlySale?: boolean
  chipPrice?: string
  shippingPrice?: string
  chipPriceOTP?: string
  shippingPriceOTP?: string
  chipPriceNVMO?: string
  pickUpInStore?: boolean
  logisticPlans?: LogisticPlans[]
}

export interface UserConfigsProps {
  nome: string
  plano: string
  valor: string
  periodo: string
  esim: boolean
  esim_pdf: string
  qr_code_esim: string
  portabilidade: boolean
}

interface CartInfoProps {
  origem: string
  ip: string
  navegador: string
  email: string
  telefone: string
  documento: string
  nome: string
  data_nascimento: string
  cep: string
  cidade: string
  estado: string
  bairro: string
  rua: string
  numero: string
  complemento: string
  referencia: string
  id_plano: string
  esim: string
  deseja_portabilidade: string
}

export interface InitReqContextProps {
  annualPlanSaleEnabled: boolean
  virtualStock: boolean
  physicalStock: boolean
  /** Configuracao da marca em /busca-uuid: se ela vende chip fisico. */
  physicalChipEnabled: boolean
  /** Configuracao da marca em /busca-uuid: se o sistema de indicacao (voucher) esta ligado. */
  referralVoucherEnabled: boolean
  /** Configuracao da marca em /busca-uuid: se ela oferece o UnixCard (ver utils/unixcard). */
  unixcardEnabled: boolean
  loadingVirtual: boolean
  loadingPhysical: boolean
  loadingBrandConfig: boolean
  statesList: StatesListProps[]
  chipInfo: ChipInfo
  loadingChipReq: boolean
  currentUserConfigs: UserConfigsProps | null
  loadingUserConfigs: boolean
  userConfigParam: UserConfigParamProps
  ip: string
  cartInfo?: CartInfoProps
  cartSearchLiberated: boolean

  // coupon info
  couponValid: boolean
  couponText: string
  couponValue: number
  couponPercent: boolean
}

export interface InitReqContext extends InitReqContextProps {
  /**
   * Derivado de physicalStock + physicalChipEnabled. E o que os componentes
   * devem consultar: nao basta ter chip em estoque, a marca precisa vender
   * chip fisico. Calculado no value do provider, nunca guardado em estado.
   */
  physicalChipAvailable: boolean
  updateInitState: (updatedValues: Partial<InitReqContextProps>) => void
}

const defaultRequestsData: InitReqContext = {
  annualPlanSaleEnabled: false,
  virtualStock: false,
  physicalStock: false,
  /*
   * Comeca habilitado porque o estado neutro e "a marca vende chip fisico".
   * O valor so vira false com uma resposta explicita da API ou com falha da
   * requisicao, tratada no catch de fetchBrandConfig.
   */
  physicalChipEnabled: true,
  /*
   * Comeca desligado: o voucher so aparece depois que /busca-uuid confirmar
   * que a marca usa o sistema de indicacao.
   */
  referralVoucherEnabled: false,
  /*
   * Comeca desligado: o UnixCard so aparece depois que /busca-uuid confirmar
   * a marca. Sem ele o cliente ainda paga com cartao de credito.
   */
  unixcardEnabled: false,
  physicalChipAvailable: false,
  loadingVirtual: true,
  loadingPhysical: true,
  loadingBrandConfig: true,
  statesList: [],
  chipInfo: {},
  loadingChipReq: true,
  currentUserConfigs: null,
  loadingUserConfigs: false,
  userConfigParam: {id: 0, tipo: ''},
  ip: '',
  cartInfo: undefined,
  cartSearchLiberated: false,
  updateInitState: () => {},

  // coupon info
  couponValid: false,
  couponText: '',
  couponValue: 0,
  couponPercent: false,
}

const RequestsContext = createContext<InitReqContext>(defaultRequestsData)

export function useInitReq() {
  return useContext(RequestsContext)
}

export function InitReqProvider({ children }: PropsWithChildren) {
  const [currentState, setCurrentState] = useState<InitReqContext>(defaultRequestsData)
  const [currentPath, setCurrentPath] = useState<string>()
  const [userConfigParam, setUserConfigParam] = useState<UserConfigParamProps>({id: 0, tipo: ''})
  const { cartInfo } = currentState
  const { currentFields: { cartId }, updateFormState } = useForm()
  const location = useLocation()

  const updateInitState = (updatedValues: Partial<InitReqContextProps>) => {
    setCurrentState(prevState => ({
      ...prevState,
      ...updatedValues
    }))
  }

  async function fetchCartSearch(id: string) {
    try {
      const { data: {
        email,
        telefone,
        documento,
        nome,
        nascimento,
        cep,
        cidade,
        estado,
        bairro,
        rua,
        numero,
        complemento,
        referencia
      }, data } = await requests.cartSearch(id)
      const formattedCep = cep.replace(/^(\d{5})(\d{3})$/, '$1-$2')
      setCurrentState(prevstate => ({ ...prevstate, cartInfo: data }))
      
      updateFormState({
        email,
        telephone: telefone.slice(2),
        ddd: telefone.slice(0, 2),
        cpfCnpj: documento,
        name: nome,
        date: dayjs(nascimento),
        cep: formattedCep,
        street: rua,
        number: numero,
        uf: estado,
        city: cidade,
        neighborhood: bairro,
        complement: complemento,
        reference: referencia,
        cityNameUpdate: cidade
      })
    } catch (error) {
      console.error('Erro ao inicializar requisições:', error)
    }
  }
    
  async function fetchStates() {
    try {
      const response = await requests.statesList()
      setCurrentState(prevstate => ({ ...prevstate, statesList: response }))
    } catch (error) {
      console.error('Erro ao inicializar requisições:', error)
    }
  }
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const id = searchParams.get('id')
    const tipo = searchParams.get('tipo')
    const param = location.pathname.split('/')[1]
    
    if (id && tipo) setUserConfigParam({id, tipo})
    setCurrentPath(param)
  }, [location, location.search])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const coupon = searchParams.get("cupom")

    const run = async () => {
      fetchStates()

      if (cartId) await fetchCartSearch(cartId)

      if (coupon) {
        // Sem carrinho (fluxo reiniciado) não há plano para validar o cupom.
        const cart = cartInfo ?? (cartId ? await fetchCartSearch(cartId) : undefined)
        if (!cart) return

        const requestData = {
          cupom: coupon,
          plano: parseInt(cart.id_plano),
          operacao: cart.esim === "1" ? "A" : "L",
        }
        const resultado = await requests.couponValidator(requestData)

        // Cupom inválido é resposta esperada, não exceção: em ambos os casos
        // (falha de rede ou valido:false) o carrinho segue sem desconto.
        if (resultado.ok && resultado.data?.valido) {
          setCurrentState(prevstate => ({
            ...prevstate,
            couponText: coupon,
            couponValid: true,
            couponValue: resultado.data.valor,
            couponPercent: resultado.data.tipo === "PERCENTUAL",
          }))
        } else {
          setCurrentState(prevstate => ({
            ...prevstate,
            couponValid: false,
            couponValue: 0
          }))
        }
      }
    }

    if (currentPath === "configure-seu-plano" && currentState.cartSearchLiberated) run()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, location.search, cartInfo?.id_plano, cartInfo?.esim, currentState.cartSearchLiberated])

  useEffect(() => {
    /*
     * So valida depois que /busca-uuid responder e confirmar que a marca usa
     * o sistema de indicacao: com o sistema desligado, ?indicacao= e ignorado.
     */
    if (currentState.loadingBrandConfig || !currentState.referralVoucherEnabled) return

    const searchParams = new URLSearchParams(location.search)
    const voucher = searchParams.get("indicacao")

    if (voucher) {
      const run = async () => {
        try {
          const { retorno, nome } = await requests.voucherValidator(voucher)

          if (retorno) {
            const firstName = nome.split(' ')[0]
            const voucherName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
            updateFormState({ isVoucherValid: true, voucherName, voucherClub: voucher })
          }
        } catch (error) {
          console.error('Voucher inválido:', error)
        }
      }
      
      if (currentPath === '' || currentPath === 'vip') run()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[currentPath, currentState.loadingBrandConfig, currentState.referralVoucherEnabled])

  useEffect(() => {
    async function fetchIp() {
      try {
        const response = await requests.currentIp()
        setCurrentState(prevstate => ({ ...prevstate, ip: response }))
      } catch (error) {
        console.error('Erro ao inicializar requisições:', error)
      }
    }

    /*
     * Os tres fetches abaixo alimentam o mesmo gate de renderizacao, entao o
     * loading sai no finally: deixa-lo dentro do try faria uma falha de rede
     * travar a tela em Skeleton para sempre. No catch o canal correspondente
     * e zerado, e assim o fallback de venda por WhatsApp aparece pela regra
     * de noStock que ja existe, sem estado de erro extra.
     */
    async function fetchVirtualStock() {
      try {
        const stock = await requests.virtualStock()
        setCurrentState(prevstate => ({ ...prevstate, virtualStock: stock > 0 }))
      } catch (error) {
        console.error('Erro ao inicializar requisições:', error)
        setCurrentState(prevstate => ({ ...prevstate, virtualStock: false }))
      } finally {
        setCurrentState(prevstate => ({ ...prevstate, loadingVirtual: false }))
      }
    }

    async function fetchPhysicalStock() {
      try {
        const stock = await requests.physicalStock()
        setCurrentState(prevstate => ({ ...prevstate, physicalStock: stock > 0 }))
      } catch (error) {
        console.error('Erro ao inicializar requisições:', error)
        setCurrentState(prevstate => ({ ...prevstate, physicalStock: false }))
      } finally {
        setCurrentState(prevstate => ({ ...prevstate, loadingPhysical: false }))
      }
    }

    async function fetchBrandConfig() {
      try {
        const { tipo, id, anual, venda_chip_fisico, sistema_indicacao } = await requests.brandConfig()
        const unixcardEnabled = isUnixcardEnabled({ tipo, id })
        setCurrentState(prevstate => ({
          ...prevstate,
          annualPlanSaleEnabled: anual || false,
          physicalChipEnabled: normalizePhysicalChipEnabled(venda_chip_fisico),
          referralVoucherEnabled: normalizeReferralVoucherEnabled(sistema_indicacao),
          unixcardEnabled,
        }))
        // Marca com UnixCard: ele vem pré-selecionado como método de pagamento.
        if (unixcardEnabled) updateFormState({ paymentMethod: 'UNIXCARD' })
      } catch (error) {
        console.error('Erro ao inicializar requisições:', error)
        setCurrentState(prevstate => ({ ...prevstate, physicalChipEnabled: false, referralVoucherEnabled: false, unixcardEnabled: false }))
      } finally {
        setCurrentState(prevstate => ({ ...prevstate, loadingBrandConfig: false }))
      }
    }

    async function fetchChip() {
      try {
        const {
          habilitar_venda_somente_chip,
          valor_chip,
          valor_frete,
          valor_chip_avulso,
          valor_frete_avulso,
          repasse_mvno_logistica_chip_avulso,
          habilitar_retirada_na_mvno,
          planos
        } = await requests.logistic()
        setCurrentState(prevstate => ({ ...prevstate, loadingChipReq: false, chipInfo: {
          chipOnlySale: habilitar_venda_somente_chip,
          chipPrice: valor_chip,
          shippingPrice: valor_frete,
          chipPriceOTP: valor_chip_avulso,
          shippingPriceOTP: valor_frete_avulso,
          chipPriceNVMO: `${repasse_mvno_logistica_chip_avulso}`,
          pickUpInStore: habilitar_retirada_na_mvno,
          logisticPlans: planos
        }}))
      } catch (error) {
        console.error('Erro ao inicializar requisições:', error)
      }
    }

    async function fetchUserConfigs() {
      setCurrentState(prevstate => ({ ...prevstate, loadingUserConfigs: true }))
      
      if (userConfigParam) {
        try {
          const userConfigs = await requests.userConfigs(userConfigParam)
          if (userConfigs) {
            setCurrentState(prevstate => ({ ...prevstate, currentUserConfigs: userConfigs }))
          } else {
            console.error("Resposta inesperada da API:", userConfigs)
          }
        } catch (error) {
          console.error("Erro ao buscar configurações de usuário:", error)
        } finally {
          setCurrentState(prevstate => ({ ...prevstate, loadingUserConfigs: false }))
        }
      }
    }

    if (currentPath === "configure-sua-conta") fetchUserConfigs()
    if (currentPath === '' || currentPath === 'vip' || currentPath === 'configure-seu-plano') {
      fetchVirtualStock()
      fetchPhysicalStock()
      fetchBrandConfig()
      fetchStates()
      fetchChip()
      fetchIp()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath])

  return (
    <RequestsContext.Provider
      value={{
        ...currentState,
        // userConfigParam vive em estado proprio: sem repassa-lo aqui, o
        // contexto devolvia o default {id: 0} e a portabilidade era
        // pre-agendada contra um id inexistente.
        userConfigParam,
        physicalChipAvailable: isPhysicalChipAvailable(currentState),
        updateInitState
      }}
    >
      {children}
    </RequestsContext.Provider>
  )
}